import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";

export const useSeo = (title, description) => {
  useEffect(() => {
    document.title = title;
    const meta = document.querySelector('meta[name="description"]');
    if (meta && description) meta.setAttribute("content", description);
  }, [title, description]);
};

export const SectionHeader = ({ eyebrow, title, desc, align = "left", dark = false, className = "" }) => (
  <Reveal className={`${align === "center" ? "mx-auto text-center" : ""} max-w-2xl ${className}`}>
    {eyebrow && (
      <p className="mb-3 text-xs font-bold uppercase tracking-widest text-tracksy-orange">{eyebrow}</p>
    )}
    <h2 className={`font-heading text-3xl font-bold tracking-tight sm:text-4xl ${dark ? "text-white" : "text-tracksy-ink"}`}>
      {title}
    </h2>
    {desc && (
      <p className={`mt-4 text-base leading-relaxed sm:text-lg ${dark ? "text-white/70" : "text-tracksy-sub"}`}>{desc}</p>
    )}
  </Reveal>
);

export const PrimaryButton = ({ to, children, testId, className = "", onClick }) => {
  const cls = `group inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold uppercase tracking-wide text-white transition-all duration-200 hover:scale-[1.03] ${className}`;
  const style = {
    backgroundColor: "var(--brand-accent)",
    boxShadow: "var(--brand-accent-glow)",
  };
  const inner = (
    <>
      {children}
      <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
    </>
  );
  return to ? (
    <Link to={to} data-testid={testId} className={cls} style={style}>{inner}</Link>
  ) : (
    <button type="button" onClick={onClick} data-testid={testId} className={cls} style={style}>{inner}</button>
  );
};

export const SecondaryButton = ({ to, children, testId, dark = false, className = "" }) => (
  <Link
    to={to}
    data-testid={testId}
    style={{ "--hover-accent": "var(--brand-accent)" }}
    className={`platform-hover inline-flex items-center justify-center gap-2 rounded-full border px-7 py-3.5 text-sm font-bold uppercase tracking-wide transition-colors duration-200 ${
      dark
        ? "border-white/20 text-white"
        : "border-black/10 bg-white text-tracksy-ink shadow-subtle"
    } ${className}`}
  >
    {children}
  </Link>
);

/** "Need 10+ trackers?" strip used across the site. */
export const FleetCta = ({ compact = false }) => (
  <Reveal>
    <div
      data-testid="fleet-cta-strip"
      className={`mx-auto flex max-w-7xl flex-col items-start justify-between gap-5 rounded-[2rem] border border-black/5 bg-white px-6 shadow-subtle sm:flex-row sm:items-center ${
        compact ? "py-6" : "py-8 sm:px-10"
      }`}
    >
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-tracksy-orange">Need 100+ trackers?</p>
        <p className="mt-1 font-heading text-xl font-bold text-tracksy-ink sm:text-2xl">Talk to Tracksy Fleet.</p>
        <p className="mt-1 text-sm text-tracksy-sub">Order any quantity online. For 100+ devices, request tailored pricing.</p>
      </div>
      <PrimaryButton to="/fleet#enquiry" testId="fleet-cta-strip-button">Request 100+ pricing</PrimaryButton>
    </div>
  </Reveal>
);
